/**
 * ISignatureProvider - Abstract interface for all signature providers
 *
 * Đây là interface gốc cho tất cả các loại ký số.
 * Các abstract class (USBSigner, HSMSigner, CASigner) sẽ implement interface này.
 */

import {
  SignType,
  SignProvider,
  SigningResult,
  SigningOptions,
  XmlSigningOptions,
  BatchSigningItem,
  BatchSigningResult,
  CertificateInfo,
} from '../types';

export interface ISignatureProvider {
  /**
   * Loại ký số
   */
  readonly signType: SignType;

  /**
   * Nhà cung cấp
   */
  readonly signProvider: SignProvider;

  /**
   * Tên hiển thị
   */
  readonly displayName: string;

  /**
   * Khởi tạo provider (kết nối plugin, server, etc.)
   * @returns Promise<boolean> - true nếu khởi tạo thành công
   */
  initialize(): Promise<boolean>;

  /**
   * Kiểm tra provider đã sẵn sàng chưa
   */
  isReady(): boolean;

  /**
   * Lấy thông tin certificate hiện tại
   */
  getCertificateInfo(): Promise<CertificateInfo | null>;

  /**
   * Ký file PDF
   * @param pdfData - PDF content (base64)
   * @param options - Tùy chọn ký
   */
  signPdf(pdfData: string, options?: SigningOptions): Promise<SigningResult>;

  /**
   * Ký file (generic - PDF, DOC, etc.)
   * @param fileData - File content (base64)
   * @param fileName - Tên file
   * @param options - Tùy chọn ký
   */
  signFile(fileData: string, fileName: string, options?: SigningOptions): Promise<SigningResult>;

  /**
   * Ký XML (cho BHYT, etc.)
   * @param xmlContent - XML content string
   * @param options - Tùy chọn ký XML
   */
  signXml(xmlContent: string, options?: XmlSigningOptions): Promise<SigningResult>;

  /**
   * Ký batch (nhiều file cùng lúc)
   * @param items - Danh sách items cần ký
   * @param options - Tùy chọn ký
   */
  signBatch(items: BatchSigningItem[], options?: SigningOptions): Promise<BatchSigningResult>;

  /**
   * Hủy bỏ / dọn dẹp resources
   */
  dispose(): void;
}

/**
 * Base abstract class implementing common functionality
 */
export abstract class BaseSignatureProvider implements ISignatureProvider {
  abstract readonly signType: SignType;
  abstract readonly signProvider: SignProvider;
  abstract readonly displayName: string;

  protected _isReady: boolean = false;

  abstract initialize(): Promise<boolean>;

  isReady(): boolean {
    return this._isReady;
  }

  abstract getCertificateInfo(): Promise<CertificateInfo | null>;
  abstract signPdf(pdfData: string, options?: SigningOptions): Promise<SigningResult>;
  abstract signFile(fileData: string, fileName: string, options?: SigningOptions): Promise<SigningResult>;
  abstract signXml(xmlContent: string, options?: XmlSigningOptions): Promise<SigningResult>;

  /**
   * Default batch implementation - ký tuần tự từng file
   * Override để ký batch thực sự nếu provider hỗ trợ
   */
  async signBatch(items: BatchSigningItem[], options?: SigningOptions): Promise<BatchSigningResult> {
    const results: BatchSigningResult['results'] = [];

    for (const item of items) {
      try {
        const result = await this.signFile(item.content, item.fileName, options);
        results.push({
          fileName: item.fileName,
          signedData: result.signedData,
          error: result.error,
        });
      } catch (error: any) {
        results.push({
          fileName: item.fileName,
          error: error.message || 'Unknown error',
        });
      }
    }

    const allSuccess = results.every((r) => r.signedData && !r.error);

    return {
      success: allSuccess,
      results,
    };
  }

  dispose(): void {
    this._isReady = false;
  }

  /**
   * Helper: Tạo error result
   */
  protected createErrorResult(error: string): SigningResult {
    return {
      success: false,
      error,
    };
  }

  /**
   * Helper: Tạo success result
   */
  protected createSuccessResult(signedData: string, metadata?: SigningResult['metadata']): SigningResult {
    return {
      success: true,
      signedData,
      metadata,
    };
  }
}
